import { gql, useQuery } from '@apollo/client'
import moment from 'moment'
import { useMemo } from 'react'

import { useDateFilter } from '../../../../dateFilter'
import { useGroupFilter } from '../../../../groupFilter'
import { IApiDataType } from '../../../types'
import useListLocationGroupDeliveryData, {
  listLocationGroupDeliveryDataConfigs,
} from '../../common/delivery/useListLocationGroupDeliveryData'

interface IDataTpye {
  customizedListLocationDelivery?: {
    nodes: {
      locationId: number
      totalOrderCount: number
      sumSubtotal: number
    }[]
  }
  customizedListLocationGroupDelivery?: {
    nodes: {
      locationGroupId: number
      totalOrderCount: number
      sumSubtotal: number
    }[]
  }
}

const query = gql`
  query customDelivery(
    $iEndDate: Date!
    $iStartDate: Date!
    $iLocationGroupIds: [Int]!
    $hasGroupBy: Boolean!
  ) {
    customizedListLocationDelivery(
      iEndDate: $iEndDate
      iStartDate: $iStartDate
      iFilter: { location_group_ids: $iLocationGroupIds }
    ) @include(if: $hasGroupBy) {
      nodes {
        locationId
        totalOrderCount
        sumSubtotal
      }
    }

    customizedListLocationGroupDelivery(
      iEndDate: $iEndDate
      iStartDate: $iStartDate
      iFilter: { location_group_ids: $iLocationGroupIds }
    ) @skip(if: $hasGroupBy) {
      nodes {
        locationGroupId
        totalOrderCount
        sumSubtotal
      }
    }
  }
`

export const bbbListLocationGroupDeliveryDataConfigs = {
  ...listLocationGroupDeliveryDataConfigs,
  totalOrderCount: 'number',
  sumSubtotal: 'price',
} as const

const YESTERDAY = moment().subtract(1, 'day')

const useBbbListLocationGroupDeliveryData = () => {
  const { data, loading } = useListLocationGroupDeliveryData()
  const { startDate, endDate: filterEndDate } = useDateFilter()
  const { groupFilter, hasGroupBy } = useGroupFilter()
  const endDate = useMemo(
    () =>
      moment(filterEndDate, 'YYYY-MM-DD').isAfter(YESTERDAY)
        ? YESTERDAY.format('YYYY-MM-DD')
        : filterEndDate,
    [filterEndDate],
  )
  const { data: customData, loading: customLoading } = useQuery<IDataTpye>(
    query,
    {
      variables: {
        iStartDate: startDate,
        iEndDate: endDate,
        iLocationGroupIds: groupFilter?.ids,
        hasGroupBy,
      },
      skip: !startDate || !endDate || !groupFilter,
    },
  )

  return {
    data: useMemo((): IApiDataType => {
      const deliveryData =
        customData?.customizedListLocationDelivery?.nodes ||
        customData?.customizedListLocationGroupDelivery?.nodes

      if (!data || !deliveryData) return null

      return data.map((d) => {
        const deliveryD = (
          deliveryData as {
            locationId?: number
            locationGroupId?: number
          }[]
        ).find((dd) =>
          'locationId' in dd
            ? dd.locationId === (d.location as { id: number })?.id
            : dd.locationGroupId === (d.locationGroup as { id: number })?.id,
        )

        return {
          ...d,
          ...deliveryD,
        }
      })
    }, [data, customData]),
    loading: loading || customLoading,
  }
}

export default useBbbListLocationGroupDeliveryData
